/**
 * accountMemberDetail.component.tsx
 * Sentriqs
 *
 * Created by: Dylan Knight
 * Copyright (c) 2024 Sentriqs, Inc. All rights reserved.
 *
**/

import React from "react";

import { Checkbox, Divider, FormControl, FormControlLabel, InputLabel, MenuItem, Select, 
  SelectChangeEvent, TextField, Tooltip} from "@mui/material"
import InfoIcon from "@mui/icons-material/Info";  

import { BillingType, MemberRole, Membership } from "../models/auth";

interface DetailProps {
  member: Membership,
  editComp: boolean,
  editBilling?: boolean,
  saveUpdate: (update: Partial<Membership>) => Promise<Membership> | undefined
}

export function AccountMemberDetailsComponent(props: DetailProps): React.ReactElement<any, any> {
  const [firstname, setFirstName] = React.useState<string>(props.member.firstname);
  const [lastname, setLastName] = React.useState<string>(props.member.lastname);
  const [complementary, setComplementary] = React.useState<boolean>(props.editComp && props.member.isComp || false);
  const [role, setRole] = React.useState<MemberRole>(props.member.memberrole);
  const [billingType, setBillingType] = React.useState<BillingType>(props.member.billingType);
  const [timer, setTimer] = React.useState<NodeJS.Timeout | null>(null);
  //const [deleting, setDeleting] = React.useState<boolean>(false);
  const lastnameRef = React.useRef<HTMLInputElement | null>(null);

  function updateValue(event: React.SyntheticEvent) {
    const inputElem = event.target as HTMLInputElement;
    const propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem.id);
      return;
    }
    const newValue = inputElem.value;
    const update: Partial<Membership> = {};
    switch (propName) {
      case "firstname":
        setFirstName(newValue);
        update.firstname = newValue;
        break;
      case "lastname":
        setLastName(newValue);
        update.lastname = newValue;
        break;
      default:
        return;
    }

    timer && clearTimeout(timer);
    setTimer(setTimeout(async () => {
      const m = await props.saveUpdate(update);
      if (m) {
        setComplementary(!!m.isComp)
        setRole(m.memberrole);
      }
      setTimer(null);
    }, 1000));
  }

  async function updateComp() {
    const newComp = !complementary;
    const update: Partial<Membership> = {
      isComp: newComp
    };

    setComplementary(newComp)
    const m = await props.saveUpdate(update);
    if (m && !!m.isComp != newComp) {
      console.error("Error setting complementary status to", newComp);
      setComplementary(!!m.isComp);
    }
  }

  function onKeyUp(event: React.KeyboardEvent): void {
    if (event.key !== "Enter") { return; }
    const inputElem = event.target as HTMLInputElement;
    const propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem.id);
      return;
    }
    if (propName === "firstname") {
      lastnameRef.current?.focus();
    } else if (propName === "lastname") {
      lastnameRef.current?.blur();
    }
  }

  function updateRole(event: SelectChangeEvent<MemberRole>): void {
    const newrole = event.target.value as MemberRole;
    setRole(newrole);
    props.saveUpdate({memberrole: newrole});
  }

  function updateBillingType(event: SelectChangeEvent<BillingType>): void {
    const newType = event.target.value as BillingType;
    setBillingType(newType);
    props.saveUpdate({billingType: newType});
  }

  const labelID = "select-label-" + props.member.userID;
  return <>
    <FormControl fullWidth className="form-row">
      <TextField label="First Name" variant="outlined" fullWidth name="firstname"
        size="small" value={firstname}
        onChange={updateValue}
        onKeyUp={onKeyUp} />
    </FormControl>
    <FormControl fullWidth className="form-row">
      <TextField inputRef={lastnameRef} label="Last Name" variant="outlined" fullWidth name="lastname"
        size="small" value={lastname}
        onChange={updateValue}
        onKeyUp={onKeyUp} />
    </FormControl>
    <FormControl fullWidth className="form-row">
      <InputLabel id={labelID + "-role"}>Role</InputLabel>
      <Select labelId={labelID + "-role"} size="small" fullWidth name="memberrole"
          label="Role"
          onChange={updateRole} value={role}>
        <MenuItem value={MemberRole.admin}>Admin</MenuItem>
        <MenuItem value={MemberRole.manager}>Manager</MenuItem>
        <MenuItem value={MemberRole.user}>User</MenuItem>
        <MenuItem value={MemberRole.guest}>Guest</MenuItem>
      </Select>
    </FormControl>
    {props.editBilling && <FormControl fullWidth className="form-row">
      <InputLabel id={labelID + "-billing"}>Billing Type</InputLabel>
      <Select labelId={labelID + "-billing"} size="small" fullWidth name="billingtype"
          label="Billing Type"
          onChange={updateBillingType} value={billingType}>
        <MenuItem value={BillingType.paid}>Paid (Default)</MenuItem>
        <MenuItem value={BillingType.guest}>Guest (Default)</MenuItem>
        <Divider id="billing-type-divider" key="billing-type-divider">Blofeld Only</Divider>
        <MenuItem value={BillingType.free}>Free</MenuItem>
      </Select>
    </FormControl>}
    {props.editComp && <>
      <FormControlLabel control={
        <Checkbox color="secondary"
            checked={complementary}
            onChange={updateComp}
            inputProps={{ "aria-label": "primary checkbox" }}
          />}
        label="Allocate Complementary Membership"
        name="complementary" />
      <Tooltip className="info-tooltip" title={
        `Mark the current membership as complementary. Your account
          will not be charged for complementary memberships.`} arrow>
        <InfoIcon className="info" />
      </Tooltip></>}
  </>;
}
